import { useState } from "react";
import { FormikProps, getIn } from "formik";
import { FiCheck } from "react-icons/fi";
import FieldWrapper from "./field-wrapper.component";
import DialogModal from "../dialog-modal/dialog-modal.component";
import { BRAND_COLOR_PRESETS } from "../../global/brand-colors.const";
import "./form-inputs.style.scss";

interface BrandColorPickerProps<Values extends object> {
	name: string;
	label: string;
	formik: FormikProps<Values>;
	disabled?: boolean;
}

export default function BrandColorPicker<Values extends object>({
	name,
	label,
	formik,
	disabled,
}: BrandColorPickerProps<Values>) {
	const [open, setOpen] = useState(false);
	const value = String(getIn(formik.values, name) ?? "");
	const isPreset = BRAND_COLOR_PRESETS.some(
		(preset) => preset.hex.toLowerCase() === value.toLowerCase(),
	);

	const handleSelect = (hex: string) => {
		formik.setFieldValue(name, hex);
		setOpen(false);
	};

	return (
		<FieldWrapper label={label} name={name} formik={formik}>
			<button
				type="button"
				className="brand-color-trigger"
				onClick={() => setOpen(true)}
				disabled={disabled}
			>
				<span
					className="brand-color-swatch"
					style={{ backgroundColor: value || "#ffffff" }}
				/>
				<span className="brand-color-hex">
					{value ? value.toUpperCase() : "Choose color"}
				</span>
			</button>

			<DialogModal
				open={open}
				onOpenChange={setOpen}
				title="Choose Brand Color"
				description="Pre-checked for legibility on printed bills and PDFs — light colors aren't offered because they wash out against the white bill background."
			>
				<div className="brand-color-grid">
					{BRAND_COLOR_PRESETS.map((preset) => {
						const selected =
							value.toLowerCase() === preset.hex.toLowerCase();
						return (
							<button
								type="button"
								key={preset.hex}
								className={`brand-color-option ${selected ? "selected" : ""}`}
								onClick={() => handleSelect(preset.hex)}
							>
								<span
									className="brand-color-swatch"
									style={{
										backgroundColor: preset.hex,
									}}
								>
									{selected && (
										<FiCheck aria-hidden="true" />
									)}
								</span>
								<span className="brand-color-name">
									{preset.name}
								</span>
							</button>
						);
					})}
					{value && !isPreset && (
						<button
							type="button"
							className="brand-color-option selected"
							onClick={() => handleSelect(value)}
						>
							<span
								className="brand-color-swatch"
								style={{ backgroundColor: value }}
							>
								<FiCheck aria-hidden="true" />
							</span>
							<span className="brand-color-name">
								Current ({value.toUpperCase()})
							</span>
						</button>
					)}
				</div>
			</DialogModal>
		</FieldWrapper>
	);
}
